import { useEffect, useMemo } from 'react';
import { ActionableNotification, InlineNotification } from '@carbon/react';
import { useSetAtom } from 'jotai';
import { DcaModal } from 'pages/dca-workspace/dca-modal';
import { triggerRefetchInsightForecastVersionsAtom } from './atoms';
import { useCreateWorkspaceAndPromote } from './use-create-workspace-and-promote';

/** What the button says while each step of the run is in flight. */
const STEP_LABELS = {
  idle: 'Create Workspace and Promote',
  creatingWorkspace: 'Creating Workspace...',
  savingForecast: 'Saving Forecast...',
  waitingForIndexing: 'Indexing Well...',
  promoting: 'Promoting...',
  done: 'Create Workspace and Promote',
} as const;

export interface WellDcaModalProps {
  uwi: string;
  wellName?: string;
  /** The forecast the editor opens on: the one selected on the page. */
  dcaForecastId?: string | null;
  open: boolean;
  onClose: () => void;
}

/**
 * The Decline Curve Analysis editor the DCA Workspace opens, on the Well page.
 *
 * The well page has no workspace to save a forecast into, so the editor's Save
 * button creates one holding this well, saves the forecast there and promotes
 * it.
 *
 * @param {WellDcaModalProps} props - The well, the forecast to open on, and open state.
 * @return {JSX.Element} The modal.
 */
export const WellDcaModal = ({
  uwi,
  wellName,
  dcaForecastId,
  open,
  onClose,
}: WellDcaModalProps) => {
  const refetchInsightForecastVersions = useSetAtom(
    triggerRefetchInsightForecastVersionsAtom
  );
  const { run, step, error, result, promotedPayload, reset, loading } =
    useCreateWorkspaceAndPromote(uwi);

  // The saved forecast is a new Insights PDP version, so the section's version
  // list has to be fetched again to include it.
  useEffect(() => {
    if (step === 'done') refetchInsightForecastVersions();
  }, [step, refetchInsightForecastVersions]);

  const saveAction = useMemo(
    () => ({
      label: STEP_LABELS[step],
      onSave: run,
      loading,
      savedPayload: promotedPayload,
    }),
    [step, run, loading, promotedPayload]
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <DcaModal
      uwi={uwi}
      wellName={wellName}
      dcaForecastId={dcaForecastId}
      open={open}
      onClose={handleClose}
      saveAction={saveAction}
      banner={
        <>
          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Create Workspace and Promote"
              subtitle={error}
            />
          )}
          {result && (
            <ActionableNotification
              kind="success"
              lowContrast
              title="Promoted"
              subtitle={`The forecast was promoted in ${result.workspaceName}.`}
              actionButtonLabel="Open Workspace"
              onActionButtonClick={() =>
                window.open(result.workspaceHref, '_blank')
              }
            />
          )}
        </>
      }
    />
  );
};
